import React, { useEffect, useState, createElement } from 'react';
import { useRestaurant } from '../context/RestaurantContext';
import { FlameIcon, GlobeIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { LANGUAGES, Language } from '../data/translations';
import { motion, AnimatePresence } from 'framer-motion';
interface HeaderProps {
  onStartOrder: () => void;
  onLookupOrder?: () => void;
}
export function Header({
  onStartOrder,
  onLookupOrder
}: HeaderProps) {
  const {
    restaurant
  } = useRestaurant();
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];
  const logoUrl = restaurant?.logo_url || '';
  const appName = restaurant?.name || '';
  const appTagline = restaurant?.tagline || '';
  // Update document metadata when restaurant loads
  useEffect(() => {
    if (!restaurant) return;
    document.title = `${restaurant.name} · ${restaurant.tagline}`;
    // Update favicon
    const existingFavicon = document.querySelector('link[rel="icon"]');
    if (existingFavicon) existingFavicon.remove();
    const favicon = document.createElement('link');
    favicon.rel = 'icon';
    favicon.type = 'image/png';
    favicon.href = restaurant.logo_url;
    document.head.appendChild(favicon);
    const appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (appleIcon) {
      ;
      (appleIcon as HTMLLinkElement).href = restaurant.logo_url;
    } else {
      const newAppleIcon = document.createElement('link');
      newAppleIcon.rel = 'apple-touch-icon';
      newAppleIcon.href = restaurant.logo_url;
      document.head.appendChild(newAppleIcon);
    }
    // Update OG tags
    const ogTags: Record<string, string> = {
      'og:title': `${restaurant.name} · ${restaurant.tagline}`,
      'og:description': `${restaurant.tagline} 🚀 Pedí en línea`,
      'og:image': restaurant.logo_url,
      'og:site_name': restaurant.name
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, [restaurant]);
  return <header className="relative mb-0 overflow-hidden" style={{
    minHeight: 280
  }} data-id="element-933">
      {/* Background image */}
      <div className="absolute inset-0 will-change-transform" data-id="element-934">
        {logoUrl && <img src={logoUrl} alt="" loading="eager" decoding="async" className="w-full h-full object-cover object-center" style={{
        willChange: 'auto'
      }} data-id="element-935" />}
        <div className="absolute inset-0 bg-black/50" data-id="element-936" />
        <div className="absolute bottom-0 left-0 right-0 h-40" style={{
        background: 'linear-gradient(to top, #0D0D0F 0%, #0D0D0F 10%, transparent 100%)'
      }} data-id="element-937" />
      </div>

      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-20" data-id="element-938">
        <button onClick={() => setIsLangOpen(!isLangOpen)} className="flex items-center gap-2 bg-carbon/80 backdrop-blur-md border border-gray-800 rounded-full px-3 py-1.5 text-white text-sm shadow-lg hover:bg-carbon transition-colors" data-id="element-939">
          <span className="text-base leading-none" data-id="element-940">{currentLang.flag}</span>
          <span className="font-medium uppercase" data-id="element-941">{currentLang.code}</span>
          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isLangOpen ? 'rotate-180' : ''}`} data-id="element-942" />
        </button>

        <AnimatePresence data-id="element-943">
          {isLangOpen && <motion.div initial={{
          opacity: 0,
          y: -10,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -10,
          scale: 0.95
        }} transition={{
          duration: 0.15
        }} className="absolute top-full right-0 mt-2 w-40 bg-surface border border-gray-800 rounded-2xl shadow-2xl overflow-hidden py-1 z-30" data-id="element-944">
              {LANGUAGES.map(lang => <button key={lang.code} onClick={() => {
            setLanguage(lang.code as Language);
            setIsLangOpen(false);
          }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${language === lang.code ? 'bg-surface-light text-white font-semibold' : 'text-gray-300 hover:bg-surface-light/50 hover:text-white'}`} data-id="element-945">
                  <span className="text-lg leading-none" data-id="element-946">{lang.flag}</span>
                  <span data-id="element-947">{lang.nativeName}</span>
                </button>)}
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Table Banner */}
      {(() => {
      const table = new URLSearchParams(window.location.search).get('table');
      return table ? <div className="absolute top-4 left-4 z-20 bg-dorado/90 backdrop-blur-md rounded-full px-4 py-1.5 flex items-center gap-2 shadow-lg" data-id="element-948">
            <span className="text-lg leading-none" data-id="element-949">🍽️</span>
            <span className="text-carbon font-bold text-sm" data-id="element-950">Mesa {table}</span>
          </div> : null;
    })()}

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 pt-10 pb-14" data-id="element-951">
        <div className="w-24 h-24 rounded-2xl shadow-2xl flex items-center justify-center mb-4 overflow-hidden border border-[#2A2A2F] bg-[#16161A]" data-id="element-952">
          {logoUrl && <img src={logoUrl} alt={appName} loading="eager" decoding="async" width={96} height={96} className="w-full h-full object-contain p-1" data-id="element-953" />}
        </div>

        <h1 className="text-4xl font-heading font-bold text-white mb-1 drop-shadow-lg tracking-tight" data-id="element-954">
          <span className="noiza-gradient-text" data-id="element-955">{appName}</span>
        </h1>

        <p className="text-[#A1A1AA] text-sm mb-6 flex items-center justify-center gap-1.5 font-medium tracking-wide drop-shadow" data-id="element-956">
          {appTagline}
        </p>

        <button onClick={onStartOrder} className="noiza-gradient text-[#0D0D0F] font-bold py-3.5 px-10 rounded-full shadow-xl shadow-[#00F5C9]/20 active:scale-95 transition-transform flex items-center gap-2 w-full max-w-xs justify-center min-h-[48px]" data-id="element-957">
          {t('orderNow')}
        </button>

        {onLookupOrder && <button onClick={onLookupOrder} className="mt-5 text-sm text-gray-400 hover:text-white underline underline-offset-4 decoration-gray-600 hover:decoration-white transition-all" data-id="element-958">
            ¿Cuál es el estado de mi pedido?
          </button>}
      </div>
    </header>;
}